export const ROLL_ALIGNMENT_INSTRUCTION = 'When beginning a new roll, align its starting pattern motif with the reference roll before cutting. Up to one full vertical repeat has been reserved for initial alignment. Trim only the length actually required.';
export const ROLL_ALIGNMENT_LIMITATION = 'Reserved length, not guaranteed waste. Workshop verification is required; horizontal seams, dye lots and exact motif alignment are not guaranteed. Unknown starting phases are not assumed to be zero.';

export function roundProcurementUp(metres, increment = 0) {
  if (!(metres > 0)) return 0;
  return increment > 0 ? Math.ceil(metres / increment - 1e-10) * increment : metres;
}

// The injected length packer is unchanged. Reserve is removed before packing,
// then mapped back to stable physical roll identities (including equal lengths).
export function optimizeWithRollAlignment({ rolls = [], demands = [], pack, supplierIncrementMeters = 0, manualWarnings = [] }) {
  const fabricIdentities = [...new Set(demands.map(d => d.fabricIdentity).filter(Boolean))];
  if (fabricIdentities.length > 1) throw new Error('Optimize one matching fabric identity at a time.');
  if (rolls.some(r => r?.fabricIdentity && fabricIdentities.length && r.fabricIdentity !== fabricIdentities[0])) {
    throw new Error('A physical roll does not match the selected fabric identity.');
  }
  const physical = rolls.map((value, index) => ({
    id: String(value?.id || `roll-${index + 1}`),
    length: Number(typeof value === 'object' ? value.usableLengthMeters : String(value).replace(/,/g, '')),
  })).filter(r => Number.isFinite(r.length) && r.length > 0);
  if (new Set(physical.map(r => r.id)).size !== physical.length) throw new Error('Each physical roll must have a unique ID.');
  if (!physical.length) throw new Error('Enter at least one usable roll length.');
  const groups = new Map();
  for (const demand of demands) {
    if (!(Number.isFinite(Number(demand.length)) && Number.isFinite(Number(demand.qty)) && demand.length > 0 && demand.qty > 0)) continue;
    const group = String(demand.matchingGroupId || 'selected-fabric');
    const repeat = demand.verticalMatchingRequired === true ? Number(demand.verticalRepeatMeters || 0) : 0;
    if (!Number.isFinite(repeat) || repeat < 0) throw new Error('Invalid vertical repeat for cutting allowance.');
    const reference = String(demand.patternReferenceId || group);
    const orientation = String(demand.patternOrientation || 'standard');
    const family = JSON.stringify([reference, repeat, orientation]);
    const key = JSON.stringify([group, family]);
    if (!groups.has(key)) groups.set(key, { group, family, reference, repeat, demands: [] });
    groups.get(key).demands.push({ ...demand, qty: Math.floor(demand.qty) });
  }
  if (!groups.size) return [];
  const references = new Map();
  for (const group of groups.values()) {
    if (group.repeat && group.demands.some(d => Math.abs(d.length / group.repeat - Math.round(d.length / group.repeat)) > 1e-6)) {
      manualWarnings = [...manualWarnings, 'Some saved cut lengths are not whole VR multiples. Verify successive panel alignment manually; the roll-start reserve is not a per-cut allowance.'];
    }
    if (references.has(group.reference) && references.get(group.reference) !== group.family) {
      manualWarnings = [...manualWarnings, 'Groups declaring a common reference have different VR or orientation. Manually verify compatibility.'];
    }
    references.set(group.reference, group.family);
  }
  let states = [{ rolls: physical.map(r => ({ ...r, remaining: r.length, allocations: [], references: [], reserve: 0 })), missing: [], referenceRollIds: {}, warnings: manualWarnings.slice() }];
  const rank = state => state.missing.reduce((sum, d) => sum + d.length * d.qty, 0);
  for (const group of groups.values()) {
    const next = [];
    for (const state of states) {
      const alreadyStarted = state.rolls.some(r => r.references.includes(group.family));
      const anchors = group.repeat && !alreadyStarted ? state.rolls.map((_, i) => i) : [-1];
      for (const anchor of anchors) {
        const entries = state.rolls.map((r, i) => ({ index: i,
          reserve: group.repeat && i !== anchor && !r.references.includes(group.family) ? group.repeat : 0,
        })).map(e => ({ ...e, capacity: state.rolls[e.index].remaining - e.reserve })).filter(e => e.capacity > 1e-9);
        if (!entries.length) continue;
        const candidates = pack({ rolls: entries.map(e => e.capacity), demands: group.demands, fabricKey: '', activeRepeatSettings: {} });
        for (const candidate of candidates) {
          const available = entries.slice();
          const used = [];
          for (const result of candidate.rollResults || []) {
            const index = available.findIndex(e => Math.abs(e.capacity - result.rollLen) < 1e-9);
            if (index < 0) throw new Error('Cutting allocation lost its physical roll identity.');
            const entry = available.splice(index, 1)[0];
            const cuts = Object.entries(result.patternInfo.pattern || {}).reduce((sum, [length, qty]) => sum + Number(length) * qty, 0);
            if (cuts > 0) used.push({ ...entry, pattern: result.patternInfo.pattern, cuts });
          }
          if (anchor >= 0 && !used.some(e => e.index === anchor)) continue;
          const copy = { rolls: state.rolls.map(r => ({ ...r, allocations: r.allocations.slice(), references: r.references.slice() })),
            referenceRollIds: { ...state.referenceRollIds },
            missing: [...state.missing, ...(candidate.remaining || []).map(d => ({ ...d, group: group.group, family: group.family, repeat: group.repeat }))],
            warnings: state.warnings.slice() };
          if (anchor >= 0) copy.referenceRollIds[group.family] = state.rolls[anchor].id;
          for (const entry of used) {
            const roll = copy.rolls[entry.index];
            if (entry.cuts + entry.reserve > roll.remaining + 1e-9) throw new Error('Cutting plan exceeds usable roll capacity.');
            if (group.repeat && roll.references.some(ref => ref !== group.family)) copy.warnings.push('A physical roll serves independent pattern references. Manually verify alignment before cutting.');
            if (group.repeat && !roll.references.includes(group.family)) roll.references.push(group.family);
            roll.remaining = Math.max(0, roll.remaining - entry.cuts - entry.reserve);
            roll.reserve += entry.reserve;
            roll.allocations.push({ matchingGroupId: group.group, patternReferenceId: group.reference,
              referenceRollId: copy.referenceRollIds[group.family] || roll.id,
              verticalRepeatMeters: group.repeat, alignmentReserveMeters: entry.reserve, pattern: entry.pattern, cuttingMeters: entry.cuts });
          }
          next.push(copy);
        }
      }
      // All original capacities can be shorter than one reserve/drop. Preserve
      // unmet demand instead of extending an existing stock roll.
      next.push({ ...state, missing: [...state.missing, ...group.demands.map(d => ({ ...d, group: group.group, family: group.family, repeat: group.repeat }))] });
    }
    next.sort((a, b) => rank(a) - rank(b) || a.warnings.length - b.warnings.length
      || a.rolls.reduce((s, r) => s + r.reserve, 0) - b.rolls.reduce((s, r) => s + r.reserve, 0));
    states = next.slice(0, 20);
  }
  const unique = new Set();
  return states.map(state => {
    const used = state.rolls.filter(r => r.allocations.length);
    const procurement = new Map();
    for (const d of state.missing) {
      if (!procurement.has(d.family)) procurement.set(d.family, { patternReferenceId: d.family, cuttingMeters: 0,
        alignmentReserveMeters: used.some(r => r.references.includes(d.family)) ? d.repeat : 0 });
      procurement.get(d.family).cuttingMeters += d.length * d.qty;
    }
    const additionalRolls = [...procurement.values()].map(p => ({ ...p,
      requiredUsableLengthMeters: p.cuttingMeters + p.alignmentReserveMeters,
      orderMeters: roundProcurementUp(p.cuttingMeters + p.alignmentReserveMeters, Number(supplierIncrementMeters)) }));
    const cutting = used.reduce((sum, r) => sum + r.allocations.reduce((n, a) => n + a.cuttingMeters, 0), 0);
    const reserve = used.reduce((sum, r) => sum + r.reserve, 0);
    const unmet = state.missing.reduce((sum, d) => sum + d.length * d.qty, 0);
    const futureReserve = additionalRolls.reduce((sum, p) => sum + p.alignmentReserveMeters, 0);
    return {
      rollResults: used.map(r => {
        const pattern = {};
        r.allocations.forEach(a => Object.entries(a.pattern).forEach(([length, qty]) => { pattern[length] = (pattern[length] || 0) + qty; }));
        return { rollId: r.id, rollLen: r.length, allocations: r.allocations, alignmentReserveMeters: r.reserve,
          patternInfo: { pattern, piecesMeters: r.length - r.remaining - r.reserve, repeatAdded: r.reserve,
            used: r.length - r.remaining, waste: r.remaining } };
      }),
      comboRolls: used.map(r => r.length), remaining: state.missing,
      rollCount: used.length, totalRollsUsed: used.length,
      baseCuttingMeters: cutting + unmet, allocatedCuttingMeters: cutting,
      allocatedAlignmentReserveMeters: reserve, alignmentAllowanceMeters: reserve + futureReserve,
      procurementAlignmentReserveMeters: futureReserve,
      totalPhysicalRequiredMeters: cutting + unmet + reserve + futureReserve,
      existingUsableStockMeters: physical.reduce((sum, r) => sum + r.length, 0),
      remainingUsableStockMeters: state.rolls.reduce((sum, r) => sum + r.remaining, 0),
      additionalProcurementMeters: additionalRolls.reduce((sum, p) => sum + p.orderMeters, 0),
      additionalRolls, procurementAssumption: 'Each listed new roll must be supplied as one continuous usable length. Splitting it requires re-optimization and may require more reserve.',
      fabricWidthsIn: [...new Set(demands.map(d => Number(d.fabricWidthIn || 0)).filter(n => n > 0))],
      verticalRepeatsMeters: [...new Set([...groups.values()].map(g => g.repeat).filter(n => n > 0))],
      horizontalRepeatsInches: [...new Set(demands.map(d => Number(d.horizontalRepeatInch || 0)).filter(n => n > 0))],
      finalRemainingMeters: unmet, requiresExtraCut: unmet > 1e-9,
      isCompleteFromAvailable: unmet <= 1e-9 && !state.warnings.length,
      manualReviewRequired: state.warnings.length > 0, warnings: [...new Set(state.warnings)],
      totalUsedMeters: cutting + reserve,
      totalWasteMeters: used.reduce((sum, r) => sum + r.remaining, 0),
      totalRollLengthMeters: used.reduce((sum, r) => sum + r.length, 0),
    };
  }).filter(solution => {
    const key = JSON.stringify(solution.rollResults.map(r => [r.rollId, r.allocations]));
    if (unique.has(key)) return false;
    unique.add(key); return true;
  }).sort((a, b) => a.finalRemainingMeters - b.finalRemainingMeters || Number(a.manualReviewRequired) - Number(b.manualReviewRequired)
    || a.rollCount - b.rollCount || a.allocatedAlignmentReserveMeters - b.allocatedAlignmentReserveMeters).slice(0, 10);
}

export function rollAlignmentSummaryLines(solution) {
  if (!solution || solution.baseCuttingMeters == null) return [];
  const m = value => `${Number(value || 0).toFixed(4)} m`;
  return [
    `Fabric width: ${(solution.fabricWidthsIn || []).join(' / ') || 'verify'} in; VR: ${(solution.verticalRepeatsMeters || []).map(m).join(' / ') || '0 m'}; HR: ${(solution.horizontalRepeatsInches || []).join(' / ') || '0'} in`,
    `Allocated physical rolls: ${solution.rollCount}`,
    `VR-adjusted cutting: ${m(solution.baseCuttingMeters)}`,
    `Allocated-roll alignment reserve: ${m(solution.allocatedAlignmentReserveMeters)} (not guaranteed waste)`,
    `Proposed-new-roll alignment reserve: ${m(solution.procurementAlignmentReserveMeters)}`,
    `Total alignment reserve: ${m(solution.alignmentAllowanceMeters)}`,
    `Total physical requirement: ${m(solution.totalPhysicalRequiredMeters)}`,
    `Existing usable stock: ${m(solution.existingUsableStockMeters)}`,
    `Additional procurement: ${m(solution.additionalProcurementMeters)}`,
    ...(solution.additionalRolls || []).map((p, i) => `Proposed new roll ${i + 1}: cuts ${m(p.cuttingMeters)} + reserve ${m(p.alignmentReserveMeters)}; order continuous usable length ${m(p.orderMeters)}`),
    `Remaining stock after reserved allocation: ${m(solution.remainingUsableStockMeters)}`,
    'Customer billable metres and GST are unchanged; this is a physical plan only.',
    ...(solution.warnings || []),
    ...(solution.additionalRolls?.length ? [solution.procurementAssumption] : []),
    ROLL_ALIGNMENT_INSTRUCTION, ROLL_ALIGNMENT_LIMITATION,
  ];
}
